import { useMutation } from '@apollo/client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { styled } from 'styled-components';
import {
  SSpanTextLg,
  SSpanTitle,
  SSpanTitleSmall,
} from '~/components/styles/styles';
import { useLingui } from '@lingui/react';
import { ButtonNext } from '~/components/ui/button-next';
import { QcuItem } from '~/components/ui/qcu-item';
import { TimerItem } from '~/components/ui/timer-item';
import { timeAnswer } from '~/data/constantes';

import { GQL_VALIDATE_ANSWER, StoredAnswer } from '~/gql/answers/answers';
import { StoredQuestion } from '~/gql/questions/questions';
import { StoredTheme } from '~/gql/theme/theme';
import { authClient } from '~/helpers/apollo';
import { useTimer } from '~/helpers/hooks/timer';
import { classic } from '~/themes';
import { formatText, renderFullPath } from '~/utils/common/utils';
import { StateAnswers } from '~/components/ui/state-answers';
import { IStateAnswerWithImage } from '~/data/types';
import { SDivFeedbackText } from '~/components/game/battle-question';
import quizImg from '@assets/images/background/quiz-background.png';
import useDebouncedCallback from '~/helpers/hooks/debounce';

type IProps = {
  currentQuestion: StoredQuestion;
  nextQuestion: () => void;
  trackingId: string;
  updateStateAnswer: (isCorrect: boolean) => void;
  theme: StoredTheme;
  onTurnChange: (nb: number) => void;
  setIsCompleted: Dispatch<SetStateAction<boolean>>;
  stateAnswers: IStateAnswerWithImage[];
  setResponsesPercent: Dispatch<SetStateAction<number | null>>;
  onNewPosition: (nb: number | undefined) => void;
  lastQuestion?: boolean;
};

function Qcu({
  currentQuestion,
  nextQuestion,
  trackingId,
  updateStateAnswer,
  setResponsesPercent,
  onTurnChange,
  setIsCompleted,
  stateAnswers,
  onNewPosition,
  lastQuestion,
}: IProps) {
  const { i18n } = useLingui();
  const [selectedAnswer, setSelectedAnswer] = useState<
    StoredAnswer | undefined
  >(undefined);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<boolean>(false);
  const { time, start, clear, stop } = useTimer();
  const [correctAnswerId, setCorrectAnswerId] = useState<string | undefined>(
    undefined
  );
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [feedbackStatus, setFeedbackStatus] = useState<
    'good' | 'wrong' | 'timeout' | undefined
  >(undefined);

  const [validateAnswer] = useMutation(GQL_VALIDATE_ANSWER, {
    client: authClient,
  });

  useEffect(() => {
    initQuestion();
    clear();
    start();
  }, [currentQuestion]);

  function initQuestion() {
    clear();
    setSelectedAnswer(undefined);
    setCorrectAnswerId(undefined);
    setSelectedAnswer(undefined);
    setFeedbackText('');
    setFeedback(false);
    setSubmitted(false);
  }

  useEffect(() => {
    if (selectedAnswer) {
      setTimeout(() => {
        handleValidate();
      }, 500);
    }
  }, [selectedAnswer]);

  useEffect(() => {
    if (time < timeAnswer) {
      return;
    }
    stop();
    handleValidate();
  }, [time]);

  function getFeedbackText(correctAnswer: string): string {
    if (time >= timeAnswer) return 'Temps écoulé...';
    if (correctAnswer === selectedAnswer?.id && time <= timeAnswer) {
      return 'Correct';
    } else {
      return 'Incorrect';
    }
  }

  async function handleValidate() {
    try {
      const { data } = await validateAnswer({
        variables: {
          validateAnswerInput: {
            questionId: currentQuestion.id,
            time: time,
            trackingId,
            answerId: selectedAnswer ? selectedAnswer.id : undefined,
          },
        },
      });
      if (data) {
        if (data.validateAnswer.tracking?.finalScore) {
          setResponsesPercent(data.validateAnswer.tracking?.finalScore);
        }

        if (time >= timeAnswer) {
          setFeedbackStatus('timeout');
        } else {
          data.validateAnswer.correctAnswerId === selectedAnswer?.id
            ? setFeedbackStatus('good')
            : setFeedbackStatus('wrong');
        }
        updateStateAnswer(
          data.validateAnswer.correctAnswerId === selectedAnswer?.id
        );
        onNewPosition(data.validateAnswer.tracking?.position);

        setCorrectAnswerId(data.validateAnswer.correctAnswerId);
        setFeedbackText(getFeedbackText(data.validateAnswer.correctAnswerId));
        onTurnChange(data.validateAnswer.turn);
        setFeedback(true);
        setSubmitted(true);
        setIsCompleted(data.validateAnswer.tracking?.isCompleted ?? false);
        //  setNewPosition(data.validateAnswer.tracking?.position || 0);
      }
    } catch {}
  }

  function handleNextQuestion() {
    initQuestion();
    nextQuestion();
  }

  const debouncedHandleClick = useDebouncedCallback(handleNextQuestion, 300);

  return (
    <Container $background={quizImg}>
      <SDivWrapperContent>
        <SDivContent>
          <SDivQuestion>
            <SDivQuestionText>
              <SSpanTextLgCustom
                $color={classic.token?.colorWhite}
                $align="center"
              >
                {formatText(currentQuestion.text)}
              </SSpanTextLgCustom>
            </SDivQuestionText>
            <SDivQuestionImg>
              <SQuestionImg
                src={renderFullPath(currentQuestion.picture.fileName)}
                alt=""
              />
            </SDivQuestionImg>
            <SDivQuestionTextTheme>
              <SSpanTitleSmallCustom $color={classic.token?.colorWhite}>
                {currentQuestion.theme.name}
              </SSpanTitleSmallCustom>
            </SDivQuestionTextTheme>
          </SDivQuestion>
          <SDivAnswers>
            {currentQuestion.answers.map((answer, index) => (
              <SAnswerWrapper key={index}>
                <SAnswer>
                  <QcuItem
                    disabled={selectedAnswer !== undefined}
                    onClick={() => {
                      stop();
                      setSelectedAnswer(answer);
                    }}
                    text={answer.text}
                    selected={selectedAnswer?.id === answer.id}
                    submitted={submitted}
                    status={
                      !feedback
                        ? 'neutral'
                        : correctAnswerId === answer.id
                          ? 'good'
                          : 'wrong'
                    }
                  />
                </SAnswer>
              </SAnswerWrapper>
            ))}
          </SDivAnswers>
        </SDivContent>
        <TimerItem timeLeft={timeAnswer - time}></TimerItem>
        <ButtonNext
          disabled={!(selectedAnswer || feedbackText)}
          onClick={(e) => {
            e.stopPropagation();
            debouncedHandleClick();
          }}
        >
          {i18n._('common.next')}
        </ButtonNext>
        <StateAnswers lastQuestion={lastQuestion} stateAnswers={stateAnswers} />
      </SDivWrapperContent>

      {feedback && (
        <SDivFeedback>
          <SDivFeedbackText $status={feedbackStatus}>
            <SSpanTitle $color={classic.token?.colorWhite}>
              {feedbackText}
            </SSpanTitle>
          </SDivFeedbackText>
        </SDivFeedback>
      )}
    </Container>
  );
}

export default Qcu;

const SSpanTitleSmallCustom = styled(SSpanTitleSmall)`
  margin-bottom: 10px;
  line-height: normal;
  font-weight: bold;
  @media screen and (max-width: 800px) {
    margin-bottom: 10px;
  }

  @media screen and (max-height: 450px) {
    margin-bottom: 3px;
  }
  @media screen and (max-width: 450px) {
    margin-bottom: 7px;
  }
`;

const SSpanTextLgCustom = styled(SSpanTextLg)`
  line-height: normal;
  font-weight: 400;
  font-size: 1.35rem;
  font-family: 'Fira sans', serif;
  margin-bottom: 0;

  margin-top: 10px;
  @media screen and (max-width: 800px) {
    font-size: 1.2rem;
    margin-top: 10px;
  }

  @media screen and (max-height: 450px) {
    font-size: 1rem;
    margin-top: 3px;
  }
  @media screen and (max-width: 450px) {
    font-size: 1rem;
    margin-top: 7px;
  }
`;

const Container = styled.div<{ $background?: string }>`
  align-items: center;
  background-color: #009cb4;
  background-image: ${({ $background }) =>
    $background ? `url('${$background}')` : 'none'};
  background-size: cover;
  mix-blend-mode: color;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  position: relative;
  width: 100%;
  padding: 60px 80px;

  @media screen and (max-width: 800px) {
    padding: 60px 60px;
  }

  @media screen and (max-height: 415px) {
    padding: 50px 30px;
  }
  @media screen and (max-width: 450px) {
    padding: 50px 10px;
  }
`;

const SDivWrapperContent = styled.div`
  height: 90%;
  width: 95%;
  position: relative;
  @media screen and (max-width: 800px) {
    width: 100%;
  }
  @media screen and (max-height: 415px) {
    width: 100%;
  }
  @media screen and (max-width: 450px) {
    width: 100%;
  }
`;

const SDivContent = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  border-radius: 40px;
  border-right: 1px solid #fff;
  border-bottom: 3px solid #fff;
  border-left: 1px solid #fff;
  background: rgba(255, 255, 255, 0.3);
  position: relative;
  overflow: hidden;
  height: 100%;
  width: 100%;
`;

const SDivFeedback = styled.div<{ $status?: 'good' | 'wrong' }>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;

const SDivQuestion = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 0 0 55%;
  padding: 40px;
  height: 100%;
  @media screen and (max-height: 450px) {
    padding: 10px;
  }
  @media screen and (max-width: 450px) {
    padding: 10px;
  }
`;
//   border: 1px solid red;

const SDivQuestionImg = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 40%;
  width: 100%;
  @media screen and (max-width: 450px) {
    height: 30%;
  }
`;
//   border: 1px solid red;

const SQuestionImg = styled.img`
  border-radius: 16px;
  object-fit: contain;
  height: 85%;
  width: 85%;

  @media screen and (max-width: 800px) {
    height: 85%;
    width: 80%;
  }
  @media screen and (max-height: 450px) {
    height: 95%;
    width: 95%;
  }
  @media screen and (max-width: 450px) {
    height: 95%;
    width: 95%;
  }

  border: 1px solid #ffffff80;
`;

const SDivQuestionText = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 45%;
  width: 95%;

  @media screen and (max-width: 450px) {
    height: 70%;
  }
`;
//   border: 1px solid blue;

const SDivQuestionTextTheme = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 20%;
  width: 95%;

  @media screen and (max-width: 450px) {
  }
`;
//   border: 1px solid blue;

const SDivAnswers = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 0 0 45%;
  height: 100%;
  padding: 40px 40px 40px 0;
  @media screen and (max-height: 450px) {
    padding: 10px 10px 10px 0;
  }
  @media screen and (max-width: 450px) {
    padding: 10px 10px 10px 0;
    justify-content: space-around;
  }
`;

const SAnswerWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 23%;
  width: 100%;
  @media screen and (max-width: 450px) {
    height: 21%;
  }
`;

const SAnswer = styled.div`
  height: 100%;
  width: 100%;
`;
